import { Component, type ErrorInfo, type ReactNode } from "react";
import { classifyClientError, classifyClientErrorType, trackEvent } from "@/lib/analytics";
import { clearStaleAppCaches, isChunkLoadError, isChunkRecoveryInProgress, reserveChunkReload } from "@/lib/chunkRecovery";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      if (isChunkRecoveryInProgress()) return;
      if (reserveChunkReload(error)) {
        void clearStaleAppCaches().finally(() => window.location.reload());
        return;
      }
    }

    trackEvent("app_error", {
      type: classifyClientErrorType("react_boundary", error),
      error_category: classifyClientError(error),
    });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="grid min-h-screen place-items-center bg-background px-6 py-12 text-foreground">
        <div className="w-full max-w-lg rounded-2xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-2xl" aria-hidden="true">🪨</div>
          <h1 className="mt-5 font-display text-2xl font-bold">GeoMiner hit an unexpected fault</h1>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            This display error does not normally change saved account or field data. Reload the latest app version, or return home and continue exploring.
          </p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              className="rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              onClick={() => window.location.reload()}
            >
              Reload GeoMiner
            </button>
            <button
              type="button"
              className="rounded-md border bg-background px-5 py-2.5 text-sm font-medium hover:bg-muted"
              onClick={() => window.location.assign("/")}
            >
              Return home
            </button>
          </div>
        </div>
      </main>
    );
  }
}
